/* eslint-disable import/order */
import React, { useEffect, useMemo, useRef } from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { I18nextProvider } from 'react-i18next';
import {
    createBrowserRouter,
    Navigate,
    Outlet,
    RouteObject,
    RouterProvider,
} from 'react-router-dom';

import {
    BaseModule,
    Channel,
    ChannelContextProvider,
    Envs,
    EventType,
    getI18n,
    GlobalErrorBoundary,
    InitialRoute,
    Modules,
    PageContextProvider,
    useChannelContext,
    useEventBusEmit,
    useGlobalErrorStore,
    useHostModule,
    useMenuStore,
    useModuleVersionStore,
    useRouterRegistry,
    VirtualRouteHookProvider,
} from 'scbk-services';
import {
    AutoLogout,
    ErrorComponent,
    GlobalComponents,
    GlobalErrorComponents,
    GlobalPopupProvider,
} from 'scbk-shared-module';
import { DeviceProvider, Loading } from 'scbk-ui-web';

import { BancaConfig, BancaModuleRoutes, BancaRouteObjects } from 'scbk-banca';
import { CardConfig, CardModuleRoutes, CardRouteObjects } from 'scbk-card';
import {
    CertificatesConfig,
    CertificatesModuleRoutes,
    CertificatesRouteObjects,
} from 'scbk-certificates';
import {
    CertificationConfig,
    CertificationModuleRoutes,
    CertificationRouteObjects,
} from 'scbk-certification';
import { CommonsConfig, CommonsModuleRoutes, CommonsRouteObjects } from 'scbk-commons';
import { DepositConfig, DepositModuleRoutes, DepositRouteObjects } from 'scbk-deposit';
import { SampleConfig, SampleModuleRoutes, SampleRouteObjects } from 'scbk-frontend-sample';
import { FundConfig, FundModuleRoutes, FundRouteObjects } from 'scbk-fund';
import { FxConfig, FxModuleRoutes, FxRouteObjects } from 'scbk-fx';
import { KeylandingConfig, KeylandingModuleRoutes, KeylandingRouteObjects } from 'scbk-keylanding';
import { LoansConfig, LoansModuleRoutes, LoansRouteObjects } from 'scbk-loans';
import { LoginConfig, LoginModuleRoutes, LoginRouteObjects } from 'scbk-login';
import { ServicingConfig, ServicingModuleRoutes, ServicingRouteObjects } from 'scbk-servicing';
import { SettingsConfig, SettingsModuleRoutes, SettingsRouteObjects } from 'scbk-settings';
import { TransferConfig, TransferModuleRoutes, TransferRouteObjects } from 'scbk-transfer';
import { TrustConfig, TrustModuleRoutes, TrustRouteObjects } from 'scbk-trust';

import { AppContextInitializer } from '@/components/initializers/AppContextInitializer';
import { BackKeyProvider } from '@/components/providers/BackKeyProvider';
import { AuthStatusSubscriber, RouteSubscriber } from '@/components/subscribers';
import { AppEventSubscriber } from '@/components/subscribers/AppEventSubscriber';
import { CoreConfig } from '@/config';
import { CommonConfig } from '@/config/commonConfig';

export const CoreModuleRoutes: React.FC = () => {
    const emit = useEventBusEmit();

    const { setHostModule } = useHostModule();
    const { setModuleVersions } = useModuleVersionStore();
    const { setErrorInfo } = useGlobalErrorStore(); // global Error Store
    const { registerModuleRouteObjects } = useRouterRegistry();
    const menuInitialized = useMenuStore((state) => state.initialized);
    const routerRef = useRef<ReturnType<typeof createBrowserRouter>>();

    const handleUnauthorizedError = () => {
        emit(EventType.SessionInvalidated);
    };

    useEffect(() => {
        // Set initial host module
        setHostModule(Modules.CORE);
    }, [setHostModule]);

    useEffect(() => {
        try {
            const moduleVersions = import.meta.env.VITE_MODULE_VERSION || {};

            console.log('Parsed moduleVersions:', moduleVersions);
            setModuleVersions(moduleVersions);
        } catch (error) {
            console.error('Error parsing module versions:', error);
        }
    }, [setModuleVersions]);

    useEffect(() => {
        console.log('CoreModuleRoutes > routeObjects : ', {
            BancaRouteObjects,
            CardRouteObjects,
            CertificatesRouteObjects,
            CertificationRouteObjects,
            CommonsRouteObjects,
            DepositRouteObjects,
            FundRouteObjects,
            FxRouteObjects,
            KeylandingRouteObjects,
            LoansRouteObjects,
            LoginRouteObjects,
            ServicingRouteObjects,
            SettingsRouteObjects,
            TransferRouteObjects,
            TrustRouteObjects,
            SampleRouteObjects,
        });

        registerModuleRouteObjects(Modules.BANCA, BancaRouteObjects);
        registerModuleRouteObjects(Modules.CARD, CardRouteObjects);
        registerModuleRouteObjects(Modules.CERTIFICATES, CertificatesRouteObjects);
        registerModuleRouteObjects(Modules.CERTIFICATION, CertificationRouteObjects);
        registerModuleRouteObjects(Modules.COMMONS, CommonsRouteObjects);
        registerModuleRouteObjects(Modules.DEPOSIT, DepositRouteObjects);
        registerModuleRouteObjects(Modules.FUND, FundRouteObjects);
        registerModuleRouteObjects(Modules.FX, FxRouteObjects);
        registerModuleRouteObjects(Modules.KEYLANDING, KeylandingRouteObjects);
        registerModuleRouteObjects(Modules.LOGIN, LoginRouteObjects);
        registerModuleRouteObjects(Modules.LOANS, LoansRouteObjects);
        registerModuleRouteObjects(Modules.SERVICING, ServicingRouteObjects);
        registerModuleRouteObjects(Modules.SETTINGS, SettingsRouteObjects);
        registerModuleRouteObjects(Modules.TRANSFER, TransferRouteObjects);
        registerModuleRouteObjects(Modules.TRUST, TrustRouteObjects);

        // 로컬, 개발, sit 환경에서만 sample routes 등록
        if (
            import.meta.env.VITE_ENVIRONMENT === Envs.LOCAL ||
            import.meta.env.VITE_ENVIRONMENT === Envs.DEV ||
            import.meta.env.VITE_ENVIRONMENT === Envs.SIT
        ) {
            registerModuleRouteObjects(Modules.SAMPLE, SampleRouteObjects);
        }
    }, [menuInitialized]);

    const ChannelHelmet = () => {
        const { channel } = useChannelContext();
        console.log('#### helmet ####');

        //모바일뱅킹/모바일웹 메타태그
        if (channel === Channel.MB || channel === Channel.MW) {
            return (
                <Helmet>
                    <meta
                        name="viewport"
                        content="width=device-width, initial-scale=1.0, maximum-scale=1.0, minimum-scale=1.0, viewport-fit=cover, user-scalable=no"
                    />
                    <meta name="format-detection" content="telephone=no" />
                    <meta name="apple-mobile-web-app-capable" content="yes" />
                    <meta
                        name="description"
                        content="모바일뱅킹으로 쉽고 빠르게 은행 서비스를 시작해보세요!"
                    />
                    <meta
                        name="google-site-verification"
                        content="w_17WahBs-ZuJJDseKVJGnZ6rOLbVkF2mmvYak7x0J8"
                    />
                    <meta
                        name="naver-site-verification"
                        content="ba8b968e712739ba71d826850322aff710b77d41"
                    />
                    <meta property="og:title" content="SC제일은행" />
                    <meta
                        property="og:image"
                        content="https://www.standardchartered.co.kr/mb/resource/images/cmm/kko_default.png"
                    />
                    <meta
                        property="og:description"
                        content="모바일뱅킹으로 은행 서비스를 시작해보세요!"
                    />
                </Helmet>
            );
        }

        //PC뱅킹 메타태그
        if (channel === Channel.IB) {
            return (
                <Helmet>
                    <meta
                        name="viewport"
                        id="viewport"
                        content="width=device-width,minimum-scale=0.37,maximum-scale=0.37,initial-scale=0.37,user-scalable=no"
                    />
                    <meta
                        name="viewport"
                        id="viewport"
                        content="width=device-width,minimum-scale=0.37,maximum-scale=1.0,initial-scale=0.37,user-scalable=yes"
                    />
                    <meta
                        name="format-detection"
                        content="telephone=no, date=no, address=no, email=no"
                    />
                    <meta httpEquiv="X-UA-Compatible" content="IE=Edge" />
                    <meta name="autocomplete" content="off" />
                </Helmet>
            );
        }
    };

    /**
     * 하위 라우트의 공통 레이아웃 담당
     */
    const RootElement = () => {
        return (
            <>
                {/* 렌더링 에러 처리 Boundary */}
                <HelmetProvider>
                    <I18nextProvider i18n={getI18n()}>
                        {/* 채널 컨텍스트 Provider */}
                        <ChannelContextProvider
                            loading={
                                <Loading loading={true} message="ChannelContextProvider Loading" />
                            }
                        >
                            {/* scbk-ui-web Provider */}
                            <DeviceProvider>
                                {/* meta 테그 추가 */}
                                <ChannelHelmet />
                                <PageContextProvider>
                                    <GlobalPopupProvider>
                                        <GlobalErrorBoundary
                                            errorPage={ErrorComponent}
                                            setErrorInfo={setErrorInfo}
                                        >
                                            <AppEventSubscriber />
                                            {/* 채널별 앱 초기화 컴포넌트 */}
                                            <AppContextInitializer>
                                                {/* 사용자 세션 동기화 처리 컴포넌트 */}
                                                <AuthStatusSubscriber />
                                                <RouteSubscriber />
                                                {/* 로딩바/다이얼로그/토스트바 */}
                                                <GlobalComponents />
                                                {/* 에러처리 컴포넌트 */}
                                                <GlobalErrorComponents />
                                                {/* 백키 프로바이더 */}
                                                <BackKeyProvider />
                                                {/* 인터넷뱅킹 자동로그아웃 Dialog */}
                                                <AutoLogout />
                                                <VirtualRouteHookProvider outlet={Outlet} />
                                            </AppContextInitializer>
                                        </GlobalErrorBoundary>
                                    </GlobalPopupProvider>
                                </PageContextProvider>
                            </DeviceProvider>
                        </ChannelContextProvider>
                    </I18nextProvider>
                </HelmetProvider>
            </>
        );
    };

    /**
     * 앱 라우터를 생성한다.
     */
    const createAppRouter = useMemo(() => {
        const devAppRouter: RouteObject[] =
            import.meta.env.VITE_ENVIRONMENT === Envs.LOCAL ||
            import.meta.env.VITE_ENVIRONMENT === Envs.DEV ||
            import.meta.env.VITE_ENVIRONMENT === Envs.SIT
                ? [
                      {
                          path: '/sample/*',
                          element: <SampleModuleRoutes key={`${Modules.SAMPLE}`} />,
                          // SampleModuleRoutes 에만 임시로 menuStore에 저장된 메뉴 정보 props로 내려준다.
                      },
                  ]
                : [];

        const appRouter = createBrowserRouter([
            {
                element: <RootElement />,
                children: [
                    {
                        index: true,
                        element: <InitialRoute navigate={Navigate} />,
                    },
                    {
                        path: '/*',
                        element: <LoginModuleRoutes />,
                    },

                    //업무 feature 별 Route 등록
                    {
                        path: '/banca/*',
                        element: <BancaModuleRoutes key={`${Modules.BANCA}`} />,
                    },
                    {
                        path: '/card/*',
                        element: <CardModuleRoutes key={`${Modules.CARD}`} />,
                    },
                    {
                        path: '/certificates/*',
                        element: <CertificatesModuleRoutes key={`${Modules.CERTIFICATES}`} />,
                    },
                    {
                        path: '/certification/*',
                        element: <CertificationModuleRoutes key={`${Modules.CERTIFICATION}`} />,
                    },
                    {
                        path: '/commons/*',
                        element: <CommonsModuleRoutes key={`${Modules.COMMONS}`} />,
                    },
                    {
                        path: '/deposit/*',
                        element: <DepositModuleRoutes key={`${Modules.DEPOSIT}`} />,
                    },
                    {
                        path: '/fund/*',
                        element: <FundModuleRoutes key={`${Modules.FUND}`} />,
                    },
                    {
                        path: '/fx/*',
                        element: <FxModuleRoutes key={`${Modules.FX}`} />,
                    },
                    {
                        path: '/keylanding/*',
                        element: <KeylandingModuleRoutes key={`${Modules.KEYLANDING}`} />,
                    },
                    {
                        path: '/loans/*',
                        element: <LoansModuleRoutes key={`${Modules.LOANS}`} />,
                    },
                    {
                        path: '/login/*',
                        element: <LoginModuleRoutes key={`${Modules.LOGIN}`} />,
                    },
                    {
                        path: '/servicing/*',
                        element: <ServicingModuleRoutes key={`${Modules.SERVICING}`} />,
                    },
                    {
                        path: '/settings/*',
                        element: <SettingsModuleRoutes key={`${Modules.SETTINGS}`} />,
                    },
                    {
                        path: '/transfer/*',
                        element: <TransferModuleRoutes key={`${Modules.TRANSFER}`} />,
                    },
                    {
                        path: '/trust/*',
                        element: <TrustModuleRoutes key={`${Modules.TRUST}`} />,
                    },
                    //개발모드인경우 노출
                    ...devAppRouter,
                    {
                        path: '*',
                        element: <ErrorComponent errorCode={'404'} />,
                    },
                ],
            },
        ]);

        return appRouter;
    }, []);

    if (!routerRef.current) {
        routerRef.current = createAppRouter;
    }

    useEffect(() => {
        console.log('### CoreModuleRoutes MOUNT', menuInitialized);
        return () => {
            console.log('### CoreModuleRoutes UNMOUNT', menuInitialized);
        };
    }, []);

    return (
        <BaseModule
            module={Modules.CORE}
            moduleConfig={CoreConfig}
            commonConfig={CommonConfig}
            subModulesConfig={{
                [Modules.BANCA]: BancaConfig,
                [Modules.CARD]: CardConfig,
                [Modules.CERTIFICATES]: CertificatesConfig,
                [Modules.CERTIFICATION]: CertificationConfig,
                [Modules.COMMONS]: CommonsConfig,
                [Modules.DEPOSIT]: DepositConfig,
                [Modules.FUND]: FundConfig,
                [Modules.FX]: FxConfig,
                [Modules.KEYLANDING]: KeylandingConfig,
                [Modules.LOANS]: LoansConfig,
                [Modules.LOGIN]: LoginConfig,
                [Modules.SERVICING]: ServicingConfig,
                [Modules.SETTINGS]: SettingsConfig,
                [Modules.TRANSFER]: TransferConfig,
                [Modules.TRUST]: TrustConfig,
                [Modules.SAMPLE]: SampleConfig,
            }}
            metaEnv={import.meta.env}
            httpProviderProps={{
                onRestDefaultError: (err) => {
                    console.error('Default error', err);
                },
                onRestUnauthorizedError: () => {
                    handleUnauthorizedError();
                },
            }}
        >
            {!menuInitialized && <Loading loading={false} />}
            {routerRef.current && <RouterProvider router={routerRef.current} />}
        </BaseModule>
    );
};
